import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { BountyCountProvider } from './context/BountyCountContext';
import Navigation from './components/Navigation';
import BountySubmitToast from './components/BountySubmitToast';
import MarqueeBar from './components/MarqueeBar';
import ConnectionBanner from './components/ConnectionBanner';
import Sidebar from './components/Sidebar';
import PasswordChangeModal from './components/PasswordChangeModal';
import CommandPalette from './components/CommandPalette';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import PostDetailPage from './pages/PostDetailPage';
import CreatePostPage from './pages/CreatePostPage';
import EditPostPage from './pages/EditPostPage';
import UserProfilePage from './pages/UserProfilePage';
import UserDirectoryPage from './pages/UserDirectoryPage';
import RankingsPage from './pages/RankingsPage';
import BookmarksPage from './pages/BookmarksPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import AdminPage from './pages/AdminPage';
import BountyBoardPage from './pages/BountyBoardPage';
import BountyDetailPage from './pages/BountyDetailPage';
import BountyFormPage from './pages/BountyFormPage';
import RewardShopPage from './pages/RewardShopPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="container mt-5">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="container mt-5">Loading...</div>;
  }

  return user?.role === 'ADMIN' ? children : <Navigate to="/" />;
};

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  // 路由變更時自動收合側邊欄（含點擊主內容區文章連結跳轉的情境），確保焦點離開選單即收起
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  return (
    <>
      <ConnectionBanner />
      <Navigation onMenuClick={() => setSidebarOpen(true)} />
      <MarqueeBar />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <CommandPalette />
      {user?.must_change_password && <PasswordChangeModal forced />}
      <main className="main-content">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/posts/:id" element={<PostDetailPage />} />

          {/* Protected Routes */}
          <Route
            path="/posts/create"
            element={
              <ProtectedRoute>
                <CreatePostPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/posts/:id/edit"
            element={
              <ProtectedRoute>
                <EditPostPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserDirectoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bookmarks"
            element={
              <ProtectedRoute>
                <BookmarksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <SubscriptionsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/rankings" element={<RankingsPage />} />

          {/* Bounty (懸賞版) Routes - 皆需登入；建立/管理權限由後端與權限矩陣控管 */}
          <Route path="/bounties" element={<ProtectedRoute><BountyBoardPage /></ProtectedRoute>} />
          <Route path="/bounties/mine" element={<ProtectedRoute><BountyBoardPage mine /></ProtectedRoute>} />
          <Route path="/bounties/create" element={<ProtectedRoute><BountyFormPage /></ProtectedRoute>} />
          <Route path="/bounties/:id" element={<ProtectedRoute><BountyDetailPage /></ProtectedRoute>} />
          <Route path="/bounties/:id/edit" element={<ProtectedRoute><BountyFormPage /></ProtectedRoute>} />

          {/* 點數兌換商城 - 需登入；上架/審核權限由後端與權限矩陣控管 */}
          <Route path="/rewards" element={<ProtectedRoute><RewardShopPage /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <SettingsProvider>
            <PermissionsProvider>
              <BountyCountProvider>
                <AppContent />
                <BountySubmitToast />
              </BountyCountProvider>
            </PermissionsProvider>
          </SettingsProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;

